import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import './PlanSelectionPage.css';
import { PRICING_CONFIG, calculateAnnualPrice, calculateAnnualWithInventoryPrice, calculateDeviceCost, calculateAnnualPriceForTS, calculateAnnualPriceForTSWithInventory } from '../config/pricing';


interface PlanOption {
  id: string;
  name: string;
  basePrice: number;
  withInventoryPrice: number;
  period: 'monthly' | 'yearly' | 'annual';
}

const plans: PlanOption[] = [
  { 
    id: 'monthly-standard', 
    name: 'IZYPOS Counter Service', 
    basePrice: PRICING_CONFIG.plans.counterService.basePrice, 
    withInventoryPrice: PRICING_CONFIG.plans.counterService.withInventoryPrice, 
    period: 'monthly'
  },
  { 
    id: 'monthly-table', 
    name: 'IZYPOS Table Service', 
    basePrice: PRICING_CONFIG.plans.tableService.basePrice, 
    withInventoryPrice: PRICING_CONFIG.plans.tableService.withInventoryPrice, 
    period: 'monthly' 
  },
  { 
    id: 'annual-standard', 
    name: 'IZYPOS Counter Service (Annual)', 
    basePrice: calculateAnnualPrice(PRICING_CONFIG.plans.counterService.basePrice), 
    withInventoryPrice: calculateAnnualWithInventoryPrice(PRICING_CONFIG.plans.counterService.basePrice), 
    period: 'annual' 
  },
  { 
    id: 'annual-table', 
    name: 'IZYPOS Table Service (Annual)', 
    basePrice: calculateAnnualPriceForTS(PRICING_CONFIG.plans.tableService.basePrice), 
    withInventoryPrice: calculateAnnualPriceForTSWithInventory(PRICING_CONFIG.plans.tableService.basePrice), 
    period: 'annual' 
  },
];

const PlanSelectionPage: React.FC = () => {
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  const [extraDevices, setExtraDevices] = useState<number>(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [customerId, setCustomerId] = useState<number | null>(null);

  const [inventoryByPlan, setInventoryByPlan] = useState<{ [planId: string]: boolean }>(() => {
    const initial: { [planId: string]: boolean } = {};
    plans.forEach(plan => { initial[plan.id] = false; }); // Default: not checked
    return initial;
  });

  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // On component mount, get customerId from session storage
  useEffect(() => {
    const savedId = sessionStorage.getItem('onboardingCustomerId');
    if (savedId) {
      setCustomerId(parseInt(savedId, 10));
    } else {
      // If no ID is found, redirect to home
      navigate('/');
    }
  }, [navigate]);

  // Scroll to top on step/option change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [selectedPlanId]);

  // Get extra devices from localStorage (set in previous step)
  useEffect(() => {
    const readExtraDevices = () => {
      const savedData = localStorage.getItem('izypostOnboardingForm');
      if (savedData) {
        const data = JSON.parse(savedData);
        if (data.extraDevices !== undefined) {
          setExtraDevices(data.extraDevices);
        } else {
          setExtraDevices(0);
        }
      } else {
        setExtraDevices(0);
      }
    };
    readExtraDevices();
    window.addEventListener('focus', readExtraDevices);
    document.addEventListener('visibilitychange', readExtraDevices);
    return () => {
      window.removeEventListener('focus', readExtraDevices);
      document.removeEventListener('visibilitychange', readExtraDevices);
    };
  }, []);

  const setupFee = PRICING_CONFIG.setup.oneTimeFee;
  const totalDevices = PRICING_CONFIG.devices.freeDevices + extraDevices;

  // For annual plans, extra devices are charged for 12 months, plan is 10 months
  const getDeviceCost = (plan: PlanOption) => {
    return calculateDeviceCost(extraDevices, plan.period === 'annual');
  };

  const calculateTotal = (plan: PlanOption) => {
    const withInventory = !!inventoryByPlan[plan.id];
    const baseCost = withInventory ? plan.withInventoryPrice : plan.basePrice;
    return baseCost + getDeviceCost(plan);
  };

  const handlePayAndContinue = async () => {
    if (!selectedPlanId || !customerId) {
      setError('Please select a plan. Customer ID is missing.');
      return;
    }
    const selectedPlan = plans.find(p => p.id === selectedPlanId);
    if (!selectedPlan) {
      setError('Selected plan not found.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      // Save plan selection and inventory to localStorage for processing page
      const onboardingForm = localStorage.getItem('izypostOnboardingForm');
      let formData = onboardingForm ? JSON.parse(onboardingForm) : {};
      formData.selectedPlanId = selectedPlanId;
      formData.inventoryByPlan = inventoryByPlan;
      localStorage.setItem('izypostOnboardingForm', JSON.stringify(formData));
      // Navigate to processing page
      navigate('/processing-subscription');
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render nothing or a loader while waiting for customerId and redirect
  if (!customerId) {
    return null;
  }

  return (
    <div className="main-content">
      <div className="plan-selection-container">
        <div className="plan-intro">
          <h1>{t('choosePlan')}</h1>
        </div>
        <div className="plan-cards">
          {plans.map(plan => (
            <div 
              key={plan.id}
              className={`plan-card ${selectedPlanId === plan.id ? 'selected' : ''}`}
              onClick={() => setSelectedPlanId(plan.id)}
            >
              {plan.id.includes('annual') && (
                <div className="recommended-label">Recommended</div>
              )}
              <div className="plan-header">
                <h2>{plan.name}</h2>
                <div className="plan-price">
                  <span className="currency">$</span>
                  <span className="amount">{!!inventoryByPlan[plan.id] ? plan.withInventoryPrice : plan.basePrice}</span>
                  <span className="period">/{plan.period === 'monthly' ? 'mo' : 'yr'}</span>
                </div>
              </div>
              <div className="plan-features">
                <div className="inventory-toggle">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={!!inventoryByPlan[plan.id]}
                      onChange={(e) => {
                        e.stopPropagation(); // Prevent card selection when clicking checkbox
                        setInventoryByPlan(prev => ({...prev, [plan.id]: e.target.checked}));
                      }}
                    />
                    {t('includeInventory')}
                    <span style={{ color: '#1976d2', fontSize: '0.98em', marginLeft: 6 }}>
                      {plan.period === 'monthly' ? t('inventoryExtraCharge') : t('inventoryExtraChargeYear')}
                    </span>
                  </label>
                </div>
                <div className="feature-list">
                <div className="feature-item">📱 {t('devices')}: {totalDevices} {extraDevices > 0 ? `(${extraDevices} extra)` : ''} <span style={{color:'#888', fontSize:'0.95em'}}>(5 free)</span></div>
                <div className="feature-item highlight">Extra Device Cost: ${getDeviceCost(plan)} {extraDevices > 0 ? `(${extraDevices} × $${PRICING_CONFIG.devices.additionalDeviceFee}${plan.period === 'annual' ? ' × 12' : ''})` : ''}</div>
                  <div className="feature-item">🕑 {t('support')}</div>
                  <div className="feature-item">☁️ {t('cloudBackup')}</div>
                  <div className="feature-item">🔄 {t('regularUpdates')}</div>
                </div>
              </div>
              <div className="plan-footer">
                <div className="setup-fee">
                  {t('oneTimeSetupFee')}: <span className="amount">${setupFee}</span>
                </div>
                <div className="total-cost">
                  {t('total')}: <span className="amount">${calculateTotal(plan) + setupFee}</span>
                </div>
                <div className="tax-note">{t('taxExcluded')}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="action-buttons">
          <button 
            className="continue-btn"
            disabled={!selectedPlanId || isSubmitting}
            onClick={handlePayAndContinue}
          >
            {isSubmitting ? t('processing') : t('payAndContinue')}
          </button>
          {error && <div className="form-error" style={{ textAlign: 'center', marginTop: '1rem' }}>{error}</div>}
        </div>
      </div>
    </div>
  );
};

export default PlanSelectionPage; 